/**
 * Course Data Transformation Utilities
 * Transforms backend course data to frontend types
 */

import { Course, CourseDetails, Lesson } from '../services/LearnerService/LearningsService';

/**
 * Backend course structure from API
 */
export interface BackendCourse {
  id: number;
  title: string;
  short_description: string;
  description: string;
  level: string;
  category_name: string;
  subcategory_name: string;
  thumbnail: string;
  total_lessons: number;
  course_duration: string;
  instructor: {
    id: string;
    name: string;
    dp: string;
  };
  avg_rating: number;
  is_enrolled: boolean;
  enrolled_date?: string;
  total_students?: number;
}

/**
 * Backend course details from API
 */
export interface BackendCourseDetails {
  id: number;
  title: string;
  short_description: string;
  description: string;
  level: string;
  thumbnail: string;
  total_lessons: number;
  course_duration: string;

  // Flat instructor fields (not nested object)
  instructor_name: string;
  instructor_dp: string;
  instructor_about?: string;
  instructor_social_links?: string;
  creator_id?: string;

  avg_rating: number;

  // Lessons at root level (not in sections)
  lessons?: Array<{
    id: number;
    title: string;
    section_id: number;
    lesson_type: 'Content-Based' | 'ILTS';
    lesson_duration?: string;
    is_completed?: boolean;
    is_unlocked?: boolean;
    content?: {
      type: 'document' | 'mp4' | 'scorm' | 'url';
      document?: string;
      mp4?: string;
      scorm?: string;
      url?: string;
    };
    ilts_info?: {
      mode: 'Online' | 'Offline';
      meet_url?: string;
      venue?: string;
      start_date?: string;
      start_time?: string;
      end_date?: string;
      end_time?: string;
      message?: string;
    };
  }>;

  // Sections with empty lessons arrays
  sections?: Array<{
    id: number;
    title: string;
    lessons_count: number;
    lessons?: any[];
  }>;

  rating_stats?: {
    total: number;
    average: number;
    breakdown: Record<number, number | null>;
  };

  requirements?: Array<{ id: number; content: string }>;
  outcomes?: Array<{ id: number; content: string }>;
  faqs?: Array<{
    id: number;
    question: string;
    answer: string;
  }>;
  reviews?: Array<{
    id: number;
    rating: number;
    review: string;
    date_added: string;
    user: {
      name: string;
      avatar: string;
    };
  }>;

  progress?: {
    total_lessons: number;
    lessons_completed: number;
    progress_percentage: string;
    last_accessed?: string | null;
  };

  total_students?: number;
  is_enrolled?: boolean;
}

/**
 * Map backend course to frontend Course interface
 * Handles different display modes: 'my-courses', 'browse', 'wishlist'
 */
export const transformBackendCourse = (
  backendCourse: BackendCourse,
  displayMode: 'my-courses' | 'browse' | 'wishlist'
): Course => {
  // Extract hours from course_duration string
  // Format: "10 hours" or "10 Hours" -> 10
  const durationMatch = backendCourse.course_duration.match(/\d+/);
  const totalHours = durationMatch ? parseInt(durationMatch[0], 10) : 0;

  const baseCourse: Course = {
    id: backendCourse.id,
    title: backendCourse.title,
    description: backendCourse.short_description || backendCourse.description,
    level: mapLevel(backendCourse.level),
    price: 'Free',
    image: backendCourse.thumbnail || '/default-course-image.jpg',
    authorLabel: backendCourse.instructor.name,
    authorLocation: backendCourse.category_name || 'General',
    displayMode,
    totalModules: backendCourse.total_lessons,
    totalHours,
    // Enrollment status from backend
    is_enrolled: backendCourse.is_enrolled,
    enrolled_date: backendCourse.enrolled_date
  };

  // Add progress fields for My Courses
  if (displayMode === 'my-courses' && backendCourse.is_enrolled) {
    return {
      ...baseCourse,
      completedCount: 0,
      totalCount: backendCourse.total_lessons,
      durationMinutes: totalHours * 60,
      progressFilled: 0,
      progressText: '0%',
      isCompleted: false
    };
  }

  return baseCourse;
};

/**
 * Map backend level string to frontend level type
 */
const mapLevel = (level: string): 'Beginner' | 'Intermediate' | 'Advanced' => {
  const normalized = level.toLowerCase().trim();

  if (normalized.includes('beginner')) return 'Beginner';
  if (normalized.includes('intermediate')) return 'Intermediate';
  if (normalized.includes('advanced')) return 'Advanced';

  return 'Beginner'; // Default fallback
};

/**
 * Transform sections and map lessons from root level into their sections
 * Lessons are provided at the root level with section_id to link back
 */
const transformSectionsWithLessons = (
  sections: Array<{
    id: number;
    title: string;
    lessons_count: number;
    lessons?: any[];
  }>,
  lessons: Array<{
    id: number;
    title: string;
    section_id: number;
    lesson_type: string;
    lesson_duration?: string;
    is_completed?: boolean;
    content?: any;
    ilts_info?: any;
  }>
) => {
  return sections.map(section => {
    // Find all lessons that belong to this section
    const sectionLessons = lessons.filter(
      lesson => lesson.section_id === section.id
    );

    // Calculate total duration for this section
    const totalMinutes = sectionLessons.reduce((sum, lesson) => {
      const match = lesson.lesson_duration?.match(/\d+/);
      return sum + (match ? parseInt(match[0], 10) : 0);
    }, 0);

    return {
      id: section.id.toString(),
      title: section.title,
      lectures: section.lessons_count,
      duration: formatDurationMinutes(totalMinutes),
      lessons: sectionLessons.map(transformLesson)
    };
  });
};

/**
 * Format duration in minutes to human-readable string
 */
const formatDurationMinutes = (totalMinutes: number): string => {
  if (totalMinutes < 60) return `${totalMinutes} mins`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

/**
 * Transform backend course details to frontend CourseDetails interface
 */
export const transformBackendCourseDetails = (
  backendData: BackendCourseDetails
): CourseDetails => {
  const durationMatch = backendData.course_duration.match(/\d+/);
  const totalHours = durationMatch ? parseInt(durationMatch[0], 10) : 0;

  return {
    id: backendData.id,
    title: backendData.title,
    subtitle: backendData.short_description,
    rating: backendData.avg_rating || 0,
    totalRatings: backendData.rating_stats?.total || 0,
    totalStudents: backendData.total_students || 0,

    // Use flat instructor fields from API
    instructor: {
      name: backendData.instructor_name || '',
      title: 'Instructor',
      avatar: backendData.instructor_dp || '',
      rating: backendData.avg_rating || 0,
      totalRatings: backendData.rating_stats?.total || 0,
      totalStudents: backendData.total_students || 0,
      totalCourses: 0,
      bio: backendData.instructor_about
        ? backendData.instructor_about.split('\n').filter(p => p.trim())
        : []
    },

    courseIncludes: [
      `${backendData.total_lessons} lessons`,
      backendData.course_duration,
      'Lifetime access',
      'Certificate of completion'
    ],

    learningPoints: backendData.outcomes?.map((o) => o.content) || [],

    // Transform sections with lessons from root level
    courseModules: transformSectionsWithLessons(
      backendData.sections || [],
      backendData.lessons || []
    ),

    // Flat lessons array for lesson navigation
    lessons: (backendData.lessons || []).map(transformLesson),

    reviews: backendData.reviews?.map(transformReview) || [],
    faqs: backendData.faqs || [],

    contentStats: {
      sections: backendData.sections?.length || 0,
      lectures: backendData.total_lessons || 0,
      totalDuration: backendData.course_duration
    },

    // Add enrollment status
    is_enrolled: backendData.is_enrolled || false
  };
};

/**
 * Transform backend section to frontend CourseModule
 */
const transformSection = (section: any) => ({
  id: section.id.toString(),
  title: section.title,
  lectures: section.lessons_count || 0,
  duration: estimateDuration(section.lessons_count),
  videos: section.lessons?.map(transformLesson) || []
});

/**
 * Transform backend lesson to frontend Lesson
 * Handles both Content-Based and ILTS lesson types
 */
const transformLesson = (lesson: any) => {
  // Base lesson properties
  const baseLesson = {
    id: lesson.id.toString(),
    title: lesson.title,
    duration: lesson.lesson_duration || '15 mins',
    isPreview: false,
    isCompleted: lesson.is_completed || false,
    isUnlocked: lesson.is_unlocked || false,
    lessonType: lesson.lesson_type
  };

  // Add content info for Content-Based lessons
  if (lesson.lesson_type === 'Content-Based' && lesson.content) {
    return {
      ...baseLesson,
      contentType: lesson.content.type,
      contentUrl: lesson.content.url ||
                  lesson.content.document ||
                  lesson.content.mp4 ||
                  lesson.content.scorm || null
    };
  }

  // Add ILTS info for ILTS lessons
  if (lesson.lesson_type === 'ILTS' && lesson.ilts_info) {
    return {
      ...baseLesson,
      iltsMode: lesson.ilts_info.mode,
      iltsUrl: lesson.ilts_info.meet_url,
      iltsVenue: lesson.ilts_info.venue,
      iltsStartDate: lesson.ilts_info.start_date,
      iltsStartTime: lesson.ilts_info.start_time,
      iltsEndDate: lesson.ilts_info.end_date,
      iltsEndTime: lesson.ilts_info.end_time,
      iltsMessage: lesson.ilts_info.message
    };
  }

  return baseLesson;
};

/**
 * Transform backend review to frontend Review
 */
const transformReview = (review: any) => ({
  id: review.id,
  name: review.user?.name || review.userName || '',
  avatar: review.user?.avatar || review.userAvatar || '',
  rating: review.rating,
  time: review.date_added ? new Date(review.date_added).toLocaleDateString() : '',
  comment: review.review || review.comment || ''
});

/**
 * Estimate duration based on lesson count
 * Assumes ~15 minutes per lesson
 */
const estimateDuration = (lessonCount: number): string => {
  if (!lessonCount) return '0 mins';

  const totalMinutes = lessonCount * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} mins`;
  if (minutes === 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
};
